/*************************************************************************************************/
/*                        TCI Application Auth                                                   */
/*************************************************************************************************/
import * as lodash from "lodash";
import {Spec} from "swagger-schema-official";
import {implementsTKeys, strEnum} from "../../types";
import {ITCIAppAutoScaleConfig} from "./tci-app-detail-env";

import {USER_ROLES} from "./tci-user";
import {TCI_APP_DEPLOYMENT_STAGE} from "./tci-app-deploy";
import {ITCIConnection, ITCIConnector} from "./tci-connections";
import {ITCIMarketplaceConnector} from "./tci-marketplace";
import {ITCITaggable} from "./tci-app-tags";
import {ITCISupplement} from "./tci-supplements";
import {ITCIMarketplaceListing} from "./tci-marketplace-v2";
import { TCIMetricsAppStatusType } from "./tci-app-metrics";

// tslint:disable: max-line-length
/**
 * TCI Tenant App type
 */
export class TCITenantAppType {
    public static bw = new TCITenantAppType("bw");
    public static node = new TCITenantAppType("node");
    public static flogo = new TCITenantAppType("flogo");
    /**
     * @deprecated DO NOT REMOVE and USE ONLY for recognition
     */
    public static mock = new TCITenantAppType("mock");
    public static scribe = new TCITenantAppType("scribe");
    /**
     * @deprecated DO NOT REMOVE and USE ONLY for recognition
     */
    public static modeler = new TCITenantAppType("modeler");
    /**
     * @deprecated DO NOT REMOVE and USE ONLY for recognition
     */
    public static mg = new TCITenantAppType("mg");
    public static liveapps = new TCITenantAppType("liveapps");
    public static ucs = new TCITenantAppType("ucs");
    public static unknown = new TCITenantAppType("unknown");
    public static all = () => TCITenantAppType.createPseudoTenant(...TCITenantAppType.getAllTypes());

    public static createPseudoTenant(...tenants: TCITenantAppType[]): TCITenantAppType {
        if (!tenants || tenants.length === 0) {
            throw Error("Needs Tenant Type arguments");
        }
        const pseudoName = tenants && tenants
            .sort((a, b) => {
                if (b.toString() > a.toString()) {
                    return 1;
                } else if (b.toString() === a.toString()) {
                    return 0;
                } else {
                    return -1;
                }
            })
            .map(tenant => tenant.toString()).join(",");
        if (tenants.length === 1) {
            return tenants[0];
        } else {
            const types = TCITenantAppType.getAllTypes().filter((type) => type.val === pseudoName);
            if (types.length > 0) {
                throw Error("Cannot use existing app types");
            }
            return new TCITenantAppType(pseudoName);
        }
    }

    /**
     * Parse JSON object to TenantAppType
     * @param jsonObj
     */
    public static fromJSON(jsonObj: any): TCITenantAppType {
        interface TenantType {
            val: string;
        }

        if (implementsTKeys<TenantType>(jsonObj, ["val"])) {
            return TCITenantAppType.fromString(jsonObj.val);
        }

    }

    /**
     * Return recognized app type, it does not mean the
     * app type is valid in TCI uses
     * @param v
     */
    public static fromString(v: string): TCITenantAppType {
        if (v && v !== "") {
            v = v.toLowerCase();
            if (v === TCITenantAppType.node.toString()) {
                return TCITenantAppType.node;
            } else if (v === TCITenantAppType.bw.toString()) {
                return TCITenantAppType.bw;
            } else if (v === TCITenantAppType.flogo.toString()) {
                return TCITenantAppType.flogo;
            } else if (v === TCITenantAppType.mg.toString()) {
                return TCITenantAppType.mg;
            } else if (v === TCITenantAppType.mock.toString()) {
                return TCITenantAppType.mock;
            } else if (v === TCITenantAppType.modeler.toString()) {
                return TCITenantAppType.modeler;
            } else if (v === TCITenantAppType.scribe.toString()) {
                return TCITenantAppType.scribe;
            } else if (v === TCITenantAppType.liveapps.toString()) {
                return TCITenantAppType.liveapps;
            } else if (v === "ipaas") {
                return TCITenantAppType.bw;
            } else if (v === "apimodeler") {
                return TCITenantAppType.modeler;
            } else if (v === "apimock") {
                return TCITenantAppType.mock;
            } else if (v === "api-mock-app") {
                return TCITenantAppType.mock;
            } else if (v === "wistudio") {
                return TCITenantAppType.flogo;
            } else if (v === "nodejs-app") {
                return TCITenantAppType.node;
            } else if (v === "ucs") {
                return TCITenantAppType.ucs;
            } else {
                return TCITenantAppType.unknown;
            }
        } else {
            return TCITenantAppType.unknown;
        }
    }

    /**
     * Only return valid app types that TCI shows on app list
     */
    public static getAllTypes(): TCITenantAppType[] {
        return <TCITenantAppType[]>[
            TCITenantAppType.node,
            TCITenantAppType.bw,
            TCITenantAppType.flogo,
            TCITenantAppType.scribe,
            TCITenantAppType.liveapps,
            TCITenantAppType.ucs
        ];
    }

    public static is(v1: any, v2: any): boolean {
        return lodash.isEqual(v1, v2);
    }

    public static getDisplayName(type: TCITenantAppType): string {
        let displayName = "";
        if (TCITenantAppType.node.equals(type)) {
            displayName = "Node.js";
        } else if (TCITenantAppType.bw.equals(type)) {
            displayName = "BusinessWorks";
        } else if (TCITenantAppType.flogo.equals(type)) {
            displayName = "Flogo";
        } else if (TCITenantAppType.scribe.equals(type)) {
            displayName = "Connect";
        } else if (TCITenantAppType.liveapps.equals(type)) {
            displayName = "Live Apps";
        }
        return displayName;
    }


    private constructor(private val: string) {
    }

    public equals(that: any): boolean {
        // Shortcut for trivial case
        if (this === that) {
            return true;
        }
        // check for primitive
        if (typeof that === "string" && that === this.val) {
            return true;
        }
        // If the that object is not a set, it is not equal to this one.
        // We use instanceof to allow any subclass of Set.
        // We could relax this test if we wanted true duck-typing.
        // Or we could strengthen it to check this.constructor == that.constructor
        // Note that instanceof properly rejects null and undefined values
        if (!(that instanceof TCITenantAppType)) {
            return false;
        }
        // If two sets don't have the same size, they're not equal

        if (this.toString() !== that.toString()) {
            return false;
        }

        return lodash.isEqual(this, that);
    }

    public valueOf(): any {
        return this.val.valueOf();
    }

    public toString(): string {
        return this.val;
    }

    /**
     * @deprecated
     * Either use getComponentModuleName when loading components and getServiceModuleName when loading services
     */
    public getSvcName(): string {
        return this.val === "flogo" ? "wistudio-svc" : `${this.val}-svc`;
    }

    /**
     * Return the module name for the services
     */
    public getServiceModuleName(): string {
        return `${this.val}-services`;
    }

    /**
     * Either use getComponentModuleName when loading components and getServiceModuleName when loading services
     */
    public getDefaultComponentModuleName(): string {
        return this.val === "flogo" ? "wistudio-svc" : `${this.val}-svc`;
    }

    /**
     * Return the module name for the components
     */
    public getComponentModuleName(routeId: string, subrouteId?: string): string {
        if (!!routeId) {
            // if (this.val === "flogo") {
            //     this.val = "wistudio";
            // }

            if (!!subrouteId) {
                return `${this.val}-${routeId}-${subrouteId}-components`;
            } else {
                return `${this.val}-${routeId}-components`;
            }
        }
        return this.getDefaultComponentModuleName();
    }

    public appliesTo(types: TCITenantAppType[]): boolean {
        return types.some((type) => type.equals(this));
    }

    public isPseudoTenant() {
        return this.val.split(",").length > 1;
    }

    public getPseudoTenants(): TCITenantAppType[] {
        return this.val.split(",").map(v => TCITenantAppType.fromString(v));
    }
}

/*************************************************************************************************/
/*                        TCI Application                                                        */

/*************************************************************************************************/
/**
 *  interface ITCIApplication
 */
export interface ITCIApplication extends ITCITaggable {
    /**
     * Unique Id of the application
     */
    id: string;
    /**
     * Name of the Application
     */
    name: string;

    /**
     * Description of the Application
     */
    description: string;
    /**
     * Application Type
     */
    type: TCITenantAppType;

    /**
     * Owner organization Id
     */
    orgId: string;

    /**
     * Owner/User Id
     */
    userId: string;

    /**
     * Pushed or Published status
     */
    publishStage: TCI_APP_DEPLOYMENT_STAGE;

    /**
     * Creation time
     */
    createdTime: number;

    /**
     * Last modification time
     */
    modifiedTime: number;

    /**
     * Last run time
     */
    lastRunTime: number;

    /**
     * Sandbox id
     */
    sandboxId: string;

    /**
     * application Information
     */
    info: ITCIAppInfo;
    /**
     * Application owner's name
     */
    ownerName: string;

    /**
     * Application owner's entity ID
     */
    owner: string;

    /**
     * The visibility of the endpoints
     */
    endpointVisibility?: string;

    /**
     * The type of the endpoints
     */
    endpointLabel?: string;

    /**
     * VPN connection id
     */
    vpnConnectionId?: string;

    /**
     * Tib Tunnel access key
     */
    tibTunnelAccessKey?: string;
    /**
     * desired instance count
     */
    desiredInstanceCount?: number;

    /**
     * auto scale config
     */
    autoScaleConfig?: ITCIAppAutoScaleConfig;

    /**
     * Error information from tenant server
     */
    error?: { message: string, statusMessage: string };

    /**
     * Id of the user that created the application
     */
    createdByName: string;

    /**
     * App initial status: Not deployed, Stopped or Running
     */
    appInitialStatus?: string;

    /**
     * Is onPremise app or not
     */
    onPremise?: boolean;
    /**
     * app deployment type
     */
    appDeploymentType?: string;
    /**
     * Internal tags for the legacy apps if they are migrationReady (IPAS-20212)
     */
    metaTags?: string[];
    /**
     * Is remote management enabled
     */
    remoteManagement?: boolean;
}

export class TCIApplicationEndpointLabel {
    public static PUBLIC = new TCIApplicationEndpointLabel("Public");
    public static CLOUD_MESH = new TCIApplicationEndpointLabel("TIBCO Cloud Mesh");

    private constructor(private _str: string) {
    }

    getString() {
        return this._str;
    }
}

export class TCIApplicationHybridStatusLabel {
    public static RUNNING = new TCIApplicationHybridStatusLabel("Running");
    public static UNKNOWN = new TCIApplicationHybridStatusLabel("");
    public static HEARTBEAT_FAILED = new TCIApplicationHybridStatusLabel("Heartbeat failed");
    public static STOPPED = new TCIApplicationHybridStatusLabel("Stopped");

    private constructor(private _str: string) {
    }

    getString() {
        return this._str;
    }
}

export class TCIApplicationDeploymentType {
    public static HYBRID = new TCIApplicationDeploymentType("Hybrid");
    public static CLOUD = new TCIApplicationDeploymentType("TIBCO Cloud");
    public static NONE = new TCIApplicationDeploymentType("None");

    private constructor(private _str: string) {
    }

    getString() {
        return this._str;
    }
}

export interface ITCIApplicationAuth {
    canCreate: boolean;
    canRead: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    role?: string;
}


export const APPLICATION_SCOPES = {
    "APP": [
        "CREATE_APP",
        "READ_APP",
        "UPDATE_APP",
        "DELETE_APP"
    ],
    "VPN": [
        "CREATE_VPN",
        "READ_VPN",
        "UPDATE_VPN",
        "DELETE_VPN"
    ],
    "HCT": [
        "CREATE_HCT",
        "READ_HCT",
        "UPDATE_HCT",
        "DELETE_HCT"
    ],
    "CONNECTIONS": [
        "CREATE_CONNECTIONS",
        "READ_CONNECTIONS",
        "UPDATE_CONNECTIONS",
        "DELETE_CONNECTIONS"
    ],
    "DOWNLOADS": [
        "DOWNLOAD_BW",
        "DOWNLOAD_TIBCLI"
    ]
};
export type APPLICATION_SCOPE_TYPE = keyof typeof APPLICATION_SCOPES;

export const APPLICATION_SCOPE_TYPES = strEnum(Object.keys(APPLICATION_SCOPES));

export const APP_AUTH_ADMIN: ITCIApplicationAuth = {
    canCreate: true,
    canRead: true,
    canUpdate: true,
    canDelete: true,
    role: USER_ROLES.ADM
};
export const APP_AUTH_USER: ITCIApplicationAuth = {
    canCreate: true,
    canRead: true,
    canUpdate: true,
    canDelete: false,
    role: USER_ROLES.USR
};

export const APP_AUTH_READ_ONLY: ITCIApplicationAuth = {
    canCreate: false,
    canRead: true,
    canUpdate: false,
    canDelete: false,
    role: USER_ROLES.RO
};

/*************************************************************************************************/
/*                        TCI Application Endpoint                                               */

/*************************************************************************************************/

/**
 * interface ITCIApplicationEndpoint
 */
export interface ITCIApplicationEndpoint {
    id: string;
    endpointName: string;
    endpointUrl?: string;
    basePath: string;
    port: number;
    protocol: string;
    pingable: boolean;
    type: string;
    ping: string;
    hasSwagger: boolean;
    name: string;
    title: string;
    description: string;
    titleError?: string;
    descriptionError?: string;
    isTitleEditing?: boolean;
    isDescriptionEditing?: boolean;
    /**
     * The URL of this endpoint
     */
    url?: string;
    /**
     * The Swagger Spec of this endpoint
     */
    swaggerJson?: SwaggerSpec;
    /**
     * The URL to retrieve the Swagger Spec
     */
    swaggerUrl?: string;
}

export const TCI_APP_PROPERTY_TYPE = strEnum([
    "string",
    "boolean",
    "datetime",
    "integer",
    "long",
    "number",
    "password"
]);

export type TCI_APP_PROPERTY_DATATYPE = keyof typeof TCI_APP_PROPERTY_TYPE;

/**
 * OAS 3.0.0 swagger spec type.
 * https://swagger.io/specification/
 */
export interface SpecOASv3 {
    openapi: string;
    info: {
        title: string;
        description?: string;
        termsOfService?: string;
        version: string;
        contact?: {
            name?: string;
            url?: string;
            email?: string;
        };
        license?: {
            name: string;
            url?: string;
        };
    };
    servers?: SpecOASv3Server[];
    paths?: any; // Define this in detail when required. Refer https://swagger.io/specification/
    components?: any; // Define this in detail when required. Refer https://swagger.io/specification/
    security?: any; // Define this in detail when required. Refer https://swagger.io/specification/
    tags?: any; // Define this in detail when required. Refer https://swagger.io/specification/
    externalDocs?: {
        description?: string;
        url: string;
    };
}

export interface SpecOASv3Server {
    url: string;
    description?: string;
    variables?: {[key: string]: {
        enum?: string[];
        default: string;
        description?: string;
    }};
}

export type SwaggerSpec = Spec | SpecOASv3;

/**
 * TCI App Endpoint visibility type
 */
export const ITCIEndpointVisibilityType = strEnum(["private", "public"]);
export type ITCIEndpointVisibilityType = keyof typeof ITCIEndpointVisibilityType;

/**
 * TCI App property
 */
export interface ITCIAppProperty {
    name: string;
    description?: string;
    datatype?: TCI_APP_PROPERTY_DATATYPE | string;
    value: string;
    // below two attribute for IPAS-20298
    readOnly?: boolean;
    tokenName?: string;
}

export interface ITCIAppProperties {
    properties: ITCIAppProperty[];
}

/**
 * TCI App Configuration
 */
export interface ITCIAppConfiguration {
    /**
     * App property prefix
     */
    propertyPrefix: string;
    /**
     * default App properties
     */
    properties: ITCIAppProperty[];

    /**
     * User system properties
     */
    userSysProperties: ITCIAppProperty[];

    /**
     * Native built-in system properties (user can't modify, only can override value via nativeSysPropertyOverrides)
     */
    nativeSysProperties?: ITCIAppProperty[];

    /**
     * App properties overrides
     */
    propertyOverrides: ITCIAppProperty[];

    /**
     * Native system properties overrides
     */
    nativeSysPropertyOverrides: ITCIAppProperty[];
}

/*************************************************************************************************/
/*                        TCI Application Info                                                   */

/*************************************************************************************************/
/**
 * interface ITCIAppInfo
 */
export interface ITCIAppInfo {
    /**
     * Number of running instances
     */
    num_instances: number;

    /**
     * Application Endpoints
     */
    end_points?: ITCIApplicationEndpoint[];
    /**
     * endpoint Ids
     */
    endpointIds?: string[];
    /**
     * Non-ping endpoints num
     */
    exposedEndpointsNum?: number;
    /**
     * tenant subtype
     */
    tenantSubType: string;
    /**
     * tenant status
     */
    tenantStatus: string;
    /**
     * legacy promoted app tag
     */
    isPackaged?: boolean;
    /**
     * legacy metrics enabled
     */
    isMetricsEnabled?: boolean;
    /**
     * For apps which are opened outside app details
     */
    externalUrl?: string;
    /**
     * optional external Id
     */
    externalId?: string;
}


export interface ITCIApplicationStatus {
    action: string;
    appId: string;
    code: string;
    details: string;
    lastError: string;
    logs: null | string[];
    message: string;
    read: boolean;
    status: string;
}

export interface ITCIApplicationOwner {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    status: string;
    timestamp: Date;
    userEntityId: string;
    originalUser?: {
        firstName: string;
        lastName: string;
        userId: string;
    };
}

export interface ITCIApplicationCopy {
    targetSbsc: string;
    targetAppName: string;
    currentOrgName: string;
    targetOrgName: string;
}

export interface ITCIApplicationsError {
    error: {
        message: string;
        statusCode: any;
        statusMessage: string;
    };
}


export interface ITCITenantAppCategory {
    label: string;
    iconUrl: string;
    id: string;
}

export interface ITCIAppDeploymentCategory {
    label: string;
    iconUrl: string;
    id: string;
}

export interface ITCIStatusFilterCategory {
    label: string;
    iconUrl: string;
    id: string;
}

export class TCITenantAppCategory implements ITCITenantAppCategory {
    public static CONNECT = new TCITenantAppCategory("CONNECT", null, "Connect", "Connect", "ng-app-ic-scribe.svg", 0);
    public static CONNECT_REPLICATION = new TCITenantAppCategory("CONNECT_REPLICATION", TCITenantAppCategory.CONNECT, "Data replication", "Data replication", "ng-app-ic-scribe.svg", 1);
    public static CONNECT_SCHEDULE = new TCITenantAppCategory("CONNECT_SCHEDULE", TCITenantAppCategory.CONNECT, "On schedule", "On a schedule", "ng-app-ic-scribe.svg", 2);
    public static CONNECT_EVENT = new TCITenantAppCategory("CONNECT_EVENT", TCITenantAppCategory.CONNECT, "On event", "On event", "ng-app-ic-scribe.svg", 3);
    public static DEVELOP = new TCITenantAppCategory("DEVELOP", null, "Develop", "", "ng-app-ic-develop.svg", 4);
    public static INTEGRATE = new TCITenantAppCategory("INTEGRATE", null, "Integrate", "Application Integration", "ng-app-ic-bw.svg", 5);
    public static SCHEMA = new TCITenantAppCategory("SCHEMA", null, "Create From Schema", "", "ng-app-ic-schema.svg", 6);
    public static AUTOMATE = new TCITenantAppCategory("AUTOMATE", null, "Automate", "", "ng-app-ic-schema.svg", 7);
    public static UCS = new TCITenantAppCategory("UCS", null, "Universal", "", "ng-app-ic-ucs.svg", 8);

    constructor(private _id: string, private _parent: ITCITenantAppCategory, private _label: string, private _longLabel: string, private _iconUrl: string, private _order: number) {
    }

    get isSubCategory(): boolean {
        return this._parent != null;
    }

    get parentCategory(): ITCITenantAppCategory {
        if (this._parent) {
            return this._parent;
        } else {
            return this;
        }
    }

    get id() {
        return this._id;
    }

    get label() {
        return this._label;
    }

    get longLabel() {
        return this._longLabel;
    }

    get iconUrl() {
        return this._iconUrl.length > 0 ? this._iconUrl : undefined;
    }

    get order() {
        return this._order;
    }

    public static fromId(id: string): ITCITenantAppCategory {
        switch (id) {
            case TCITenantAppCategory.CONNECT.id:
                return TCITenantAppCategory.CONNECT;
            case TCITenantAppCategory.CONNECT_EVENT.id:
                return TCITenantAppCategory.CONNECT_EVENT;
            case TCITenantAppCategory.CONNECT_SCHEDULE.id:
                return TCITenantAppCategory.CONNECT_SCHEDULE;
            case TCITenantAppCategory.CONNECT_REPLICATION.id:
                return TCITenantAppCategory.CONNECT_REPLICATION;
            case TCITenantAppCategory.INTEGRATE.id:
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppCategory.DEVELOP.id:
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppCategory.SCHEMA.id:
                return TCITenantAppCategory.SCHEMA;
            case TCITenantAppCategory.AUTOMATE.id:
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppCategory.UCS.id:
                return TCITenantAppCategory.UCS;
        }
    }

    public static fromConnection(conn: ITCIConnection): ITCITenantAppCategory {
        switch (conn.tenantType.toString()) {
            case TCITenantAppType.scribe.toString():
                return TCITenantAppCategory.CONNECT;
            case TCITenantAppType.flogo.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.bw.toString():
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppType.node.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.liveapps.toString():
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppType.ucs.toString():
                return TCITenantAppCategory.UCS;
        }
    }

    public static fromSupplement(supp: ITCISupplement): ITCITenantAppCategory {
        switch (supp.tenantType.toString()) {
            case TCITenantAppType.scribe.toString():
                return TCITenantAppCategory.CONNECT;
            case TCITenantAppType.flogo.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.bw.toString():
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppType.node.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.liveapps.toString():
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppType.ucs.toString():
                return TCITenantAppCategory.UCS;
        }
    }

    public static fromConnector(conn: ITCIConnector): ITCITenantAppCategory {
        switch (conn.tenantType.toString()) {
            case TCITenantAppType.scribe.toString():
                return TCITenantAppCategory.CONNECT;
            case TCITenantAppType.flogo.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.bw.toString():
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppType.node.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.liveapps.toString():
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppType.ucs.toString():
                return TCITenantAppCategory.UCS;
        }
    }

    public static fromMarketplaceConnector(conn: ITCIMarketplaceConnector): ITCITenantAppCategory {
        switch (conn.tenantType.toString()) {
            case TCITenantAppType.scribe.toString():
                return TCITenantAppCategory.CONNECT;
            case TCITenantAppType.flogo.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.bw.toString():
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppType.node.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.liveapps.toString():
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppType.ucs.toString():
                return TCITenantAppCategory.UCS;
        }
    }

    public static fromMarketplaceListing(listing: ITCIMarketplaceListing): ITCITenantAppCategory {
        switch (listing.tenantType.toString()) {
            case TCITenantAppType.scribe.toString():
                return TCITenantAppCategory.CONNECT;
            case TCITenantAppType.flogo.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.bw.toString():
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppType.node.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.liveapps.toString():
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppType.ucs.toString():
                    return TCITenantAppCategory.UCS;
            default:
                return null;
        }
    }

    public static fromApplication(app: ITCIApplication): ITCITenantAppCategory {
        switch (app.type.toString()) {
            case TCITenantAppType.scribe.toString(): {
                if (app.info.tenantSubType) {
                    switch (app.info.tenantSubType) {
                        case "Replication":
                            return TCITenantAppCategory.CONNECT_REPLICATION;
                        case "Integration":
                            return TCITenantAppCategory.CONNECT_SCHEDULE;
                        case "IntegrationEvent":
                            return TCITenantAppCategory.CONNECT_EVENT;
                        default:
                            return TCITenantAppCategory.CONNECT;
                    }
                }
                return TCITenantAppCategory.CONNECT;
            }
            case TCITenantAppType.flogo.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.bw.toString():
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppType.node.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.liveapps.toString():
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppType.ucs.toString():
                return TCITenantAppCategory.UCS;
        }
    }

    public static fromTenantType(tenantType: TCITenantAppType): ITCITenantAppCategory {
        switch (tenantType.toString()) {
            case TCITenantAppType.scribe.toString():
                return TCITenantAppCategory.CONNECT;
            case TCITenantAppType.flogo.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.bw.toString():
                return TCITenantAppCategory.INTEGRATE;
            case TCITenantAppType.node.toString():
                return TCITenantAppCategory.DEVELOP;
            case TCITenantAppType.liveapps.toString():
                return TCITenantAppCategory.AUTOMATE;
            case TCITenantAppType.ucs.toString():
                return TCITenantAppCategory.UCS;
        }
    }

    public static getAllCategories(): ITCITenantAppCategory[] {
        const cats = <TCITenantAppCategory[]>[
            TCITenantAppCategory.CONNECT,
            TCITenantAppCategory.CONNECT_REPLICATION,
            TCITenantAppCategory.CONNECT_SCHEDULE,
            TCITenantAppCategory.CONNECT_EVENT,
            TCITenantAppCategory.DEVELOP,
            TCITenantAppCategory.INTEGRATE,
            TCITenantAppCategory.SCHEMA,
            TCITenantAppCategory.AUTOMATE,
        ];
        return cats;
    }

    public static getAllConnectCategories(): ITCITenantAppCategory[] {
        const cats = <TCITenantAppCategory[]>[
            TCITenantAppCategory.CONNECT,
            TCITenantAppCategory.CONNECT_REPLICATION,
            TCITenantAppCategory.CONNECT_SCHEDULE,
            TCITenantAppCategory.CONNECT_EVENT,
            TCITenantAppCategory.DEVELOP,
            TCITenantAppCategory.INTEGRATE,
            TCITenantAppCategory.SCHEMA,
            TCITenantAppCategory.UCS
        ];
        return cats;
    }
}

export class TCIAppDeploymentCategory implements ITCIAppDeploymentCategory {
    public static CLOUD = new TCIAppDeploymentCategory("CLOUD", null, "TIBCO Cloud", "", "", 0);
    public static HYBRID = new TCIAppDeploymentCategory("HYBRID", null, "Hybrid", "", "", 1);
    public static NONE = new TCIAppDeploymentCategory("NONE", null, "None", "", "", 2);

    constructor(private _id: string, private _parent: ITCIAppDeploymentCategory, private _label: string, private _longLabel: string, private _iconUrl: string, private _order: number) {
    }

    get isSubCategory(): boolean {
        return this._parent != null;
    }

    get parentCategory(): ITCIAppDeploymentCategory {
        if (this._parent) {
            return this._parent;
        } else {
            return this;
        }
    }

    get id() {
        return this._id;
    }

    get label() {
        return this._label;
    }

    get longLabel() {
        return this._longLabel;
    }

    get iconUrl() {
        return this._iconUrl.length > 0 ? this._iconUrl : undefined;
    }

    get order() {
        return this._order;
    }

    public static fromId(id: string): ITCIAppDeploymentCategory {
        switch (id) {
            case TCIAppDeploymentCategory.HYBRID.id:
                return TCIAppDeploymentCategory.HYBRID;
            case TCIAppDeploymentCategory.CLOUD.id:
                return TCIAppDeploymentCategory.CLOUD;
            case TCIAppDeploymentCategory.NONE.id:
                return TCIAppDeploymentCategory.NONE;
        }
    }

    public static fromOnPremise(app: ITCIApplication): ITCIAppDeploymentCategory {
        if (app.type.equals(TCITenantAppType.scribe)) {
            switch (app.appDeploymentType.toString()) {
                case TCIAppDeploymentCategory.HYBRID.label:
                    return TCIAppDeploymentCategory.HYBRID;
                case TCIAppDeploymentCategory.CLOUD.label:
                    return TCIAppDeploymentCategory.CLOUD;
                case TCIAppDeploymentCategory.NONE.label:
                    return TCIAppDeploymentCategory.NONE;
            }
        } else {
            return app.onPremise ? TCIAppDeploymentCategory.HYBRID : TCIAppDeploymentCategory.CLOUD;
        }
    }

    public static getAllAppTypes(): ITCIAppDeploymentCategory[] {
        return <TCIAppDeploymentCategory[]>[
            TCIAppDeploymentCategory.CLOUD,
            TCIAppDeploymentCategory.HYBRID,
            TCIAppDeploymentCategory.NONE
        ];
    }
}

export class TCIStatusFilterCategory implements ITCIStatusFilterCategory {
    public static GOOD = new TCIStatusFilterCategory("GOOD", null, "Good", "", "", 0);
    public static ERROR = new TCIStatusFilterCategory("ERROR", null, "Error", "", "", 1);
    public static WARNING = new TCIStatusFilterCategory("WARNING", null, "Warning", "", "", 2);
    public static NOT_RUNNING = new TCIStatusFilterCategory("NOT_RUNNING", null, "Not running", "", "", 3);

    constructor(private _id: string, private _parent: TCIStatusFilterCategory, private _label: string, private _longLabel: string, private _iconUrl: string, private _order: number) {
    }

    get isSubCategory(): boolean {
        return this._parent != null;
    }

    get parentCategory(): TCIStatusFilterCategory {
        if (this._parent) {
            return this._parent;
        } else {
            return this;
        }
    }

    get id() {
        return this._id;
    }

    get label() {
        return this._label;
    }

    get longLabel() {
        return this._longLabel;
    }

    get iconUrl() {
        return this._iconUrl.length > 0 ? this._iconUrl : undefined;
    }

    get order() {
        return this._order;
    }

    public static fromId(id: string): ITCIStatusFilterCategory {
        switch (id) {
            case TCIStatusFilterCategory.GOOD.id:
                return TCIStatusFilterCategory.GOOD;
            case TCIStatusFilterCategory.ERROR.id:
                return TCIStatusFilterCategory.ERROR;
            case TCIStatusFilterCategory.WARNING.id:
                return TCIStatusFilterCategory.WARNING;
            case TCIStatusFilterCategory.NOT_RUNNING.id:
                return TCIStatusFilterCategory.NOT_RUNNING;
        }
    }

    public static fromLabel(status: string): ITCIStatusFilterCategory {
        switch (status) {
            case TCIStatusFilterCategory.GOOD.label:
                return TCIStatusFilterCategory.GOOD;
            case TCIStatusFilterCategory.ERROR.label:
                return TCIStatusFilterCategory.ERROR;
            case TCIStatusFilterCategory.WARNING.label:
                return TCIStatusFilterCategory.WARNING;
            case TCIStatusFilterCategory.NOT_RUNNING.label:
                return TCIStatusFilterCategory.NOT_RUNNING;
        }
    }

    public static getAllAppStatus(): ITCIStatusFilterCategory[] {
        return <TCIStatusFilterCategory[]>[
            TCIStatusFilterCategory.GOOD,
            TCIStatusFilterCategory.ERROR,
            TCIStatusFilterCategory.WARNING,
            TCIStatusFilterCategory.NOT_RUNNING
        ];
    }

    public static fromMetricsStatus(status: string): ITCIStatusFilterCategory {
        switch (status) {
            case TCIMetricsAppStatusType.good:
                return TCIStatusFilterCategory.GOOD;
            case TCIMetricsAppStatusType.error:
                return TCIStatusFilterCategory.ERROR;
            case TCIMetricsAppStatusType.warning:
                return TCIStatusFilterCategory.WARNING;
            case TCIMetricsAppStatusType.stopped:
                return TCIStatusFilterCategory.NOT_RUNNING;
        }
    }
}

export function MapAppType(appInfo: ITCIApplication): ITCIApplication {
    if (!appInfo.error) {
        appInfo.type = TCITenantAppType.fromString((<any>appInfo.type).val);
    }
    return appInfo;
}
